/**
 * Componente de Modal Reutilizável
 * Fornece funcionalidades padronizadas para criação e gerenciamento de modais
 */

/**
 * Configuração padrão para modais
 */
const defaultModalConfig = {
    size: 'md', // sm, md, lg, xl
    backdrop: true,
    keyboard: true,
    focus: true,
    show: true,
    centered: false,
    scrollable: false,
    fade: true,
    closeButton: true,
    footerButtons: [],
    onShow: null,
    onHide: null,
    onShown: null,
    onHidden: null
};

/**
 * Tipos de modal predefinidos
 */
const modalTypes = {
    info: {
        headerClass: 'bg-info text-white',
        icon: 'fas fa-info-circle'
    },
    success: {
        headerClass: 'bg-success text-white',
        icon: 'fas fa-check-circle'
    },
    warning: {
        headerClass: 'bg-warning text-dark',
        icon: 'fas fa-exclamation-triangle'
    },
    error: {
        headerClass: 'bg-danger text-white',
        icon: 'fas fa-times-circle'
    },
    confirm: {
        headerClass: 'bg-primary text-white',
        icon: 'fas fa-question-circle'
    }
};

/**
 * Classe principal para gerenciamento de modais
 */
export class ModalManager {
    constructor(config = {}) {
        this.config = { ...defaultModalConfig, ...config };
        this.modal = null;
        this.modalElement = null;
        this.id = this.generateId();
    }
    
    generateId() {
        return 'modal-' + Math.random().toString(36).substr(2, 9);
    }
    
    create(title, content, type = null) {
        this.destroy(); // Remove modal anterior se existir
        
        const typeConfig = type ? modalTypes[type] : {};
        
        const modalHtml = this.buildModalHtml(title, content, typeConfig);
        $('body').append(modalHtml);
        
        this.modalElement = $(`#${this.id}`);
        this.setupEventListeners();
        
        // Inicializar modal do Bootstrap
        this.modal = new bootstrap.Modal(this.modalElement[0], {
            backdrop: this.config.backdrop,
            keyboard: this.config.keyboard,
            focus: this.config.focus
        });
        
        if (this.config.show) {
            this.show();
        }
        
        return this;
    }
    
    buildModalHtml(title, content, typeConfig = {}) {
        const sizeClass = this.config.size !== 'md' ? `modal-${this.config.size}` : '';
        const centeredClass = this.config.centered ? 'modal-dialog-centered' : '';
        const scrollableClass = this.config.scrollable ? 'modal-dialog-scrollable' : '';
        const fadeClass = this.config.fade ? 'fade' : '';
        const headerClass = typeConfig.headerClass || '';
        const icon = typeConfig.icon ? `<i class="${typeConfig.icon} me-2"></i>` : '';
        
        const closeButton = this.config.closeButton ? 
            '<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"><i class="sg sg-x"></i></button>' : '';
        
        const footerHtml = this.buildFooterHtml();
        
        return `
            <div class="modal ${fadeClass}" id="${this.id}" tabindex="-1" aria-labelledby="${this.id}-label" aria-hidden="true">
                <div class="modal-dialog ${sizeClass} ${centeredClass} ${scrollableClass}">
                    <div class="modal-content">
                        <div class="modal-header ${headerClass}">
                            <h5 class="modal-title" id="${this.id}-label">
                                ${icon}${title}
                            </h5>
                            ${closeButton}
                        </div>
                        <div class="modal-body">
                            ${content}
                        </div>
                        ${footerHtml}
                    </div>
                </div>
            </div>
        `;
    }
    
    buildFooterHtml() {
        if (!this.config.footerButtons || this.config.footerButtons.length === 0) {
            return '';
        }
        
        const buttonsHtml = this.config.footerButtons.map(button => {
            const btnClass = button.class || 'btn-secondary';
            const btnId = button.id || '';
            const btnData = button.data ? Object.entries(button.data)
                .map(([key, value]) => `data-${key}="${value}"`).join(' ') : '';
            
            return `<button type="button" class="btn ${btnClass}" ${btnId ? `id="${btnId}"` : ''} ${btnData}>
                        ${button.text}
                    </button>`;
        }).join('');
        
        return `<div class="modal-footer">${buttonsHtml}</div>`;
    }
    
    setupEventListeners() {
        if (this.config.onShow) {
            this.modalElement.on('show.bs.modal', this.config.onShow);
        }
        
        if (this.config.onShown) {
            this.modalElement.on('shown.bs.modal', this.config.onShown);
        }
        
        if (this.config.onHide) {
            this.modalElement.on('hide.bs.modal', this.config.onHide);
        }
        
        if (this.config.onHidden) {
            this.modalElement.on('hidden.bs.modal', this.config.onHidden);
        }
        
        // Auto-destroy quando o modal é fechado
        this.modalElement.on('hidden.bs.modal', () => {
            this.destroy();
        });
        
        // Setup button event listeners
        this.config.footerButtons?.forEach(button => {
            if (button.id && button.onClick) {
                this.modalElement.find(`#${button.id}`).on('click', button.onClick);
            }
        });
    }
    
    show() {
        if (this.modal) {
            this.modal.show();
        }
        return this;
    }
    
    hide() {
        if (this.modal) {
            this.modal.hide();
        }
        return this;
    }
    
    destroy() {
        if (this.modal) {
            this.modal.dispose();
        }
        if (this.modalElement) {
            this.modalElement.remove();
        }
        this.modal = null;
        this.modalElement = null;
    }
    
    updateContent(content) {
        if (this.modalElement) {
            this.modalElement.find('.modal-body').html(content);
        }
        return this;
    }
    
    updateTitle(title) {
        if (this.modalElement) {
            this.modalElement.find('.modal-title').html(title);
        }
        return this;
    }
}

/**
 * Factory functions para tipos específicos de modal
 */
export function createInfoModal(title, content, config = {}) {
    return new ModalManager(config).create(title, content, 'info');
}

export function createSuccessModal(title, content, config = {}) {
    return new ModalManager(config).create(title, content, 'success');
}

export function createWarningModal(title, content, config = {}) {
    return new ModalManager(config).create(title, content, 'warning');
}

export function createErrorModal(title, content, config = {}) {
    return new ModalManager(config).create(title, content, 'error');
}

export function createConfirmModal(title, content, onConfirm, onCancel = null, config = {}) {
    const modalConfig = {
        ...config,
        footerButtons: [
            {
                text: 'Cancelar',
                class: 'btn-secondary',
                id: 'btn-cancel',
                onClick: () => {
                    if (onCancel) onCancel();
                }
            },
            {
                text: 'Confirmar',
                class: 'btn-primary',
                id: 'btn-confirm',
                onClick: onConfirm
            }
        ]
    };
    
    return new ModalManager(modalConfig).create(title, content, 'confirm');
}

/**
 * Modal de loading
 */
export function createLoadingModal(title = 'Carregando...', content = 'Por favor, aguarde...') {
    const config = {
        backdrop: 'static',
        keyboard: false,
        closeButton: false,
        size: 'sm',
        centered: true
    };
    
    const loadingContent = `
        <div class="text-center">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Carregando...</span>
            </div>
            <div class="mt-3">${content}</div>
        </div>
    `;
    
    return new ModalManager(config).create(title, loadingContent);
}

/**
 * Modal de formulário
 */
export function createFormModal(title, formHtml, onSubmit, config = {}) {
    const modalConfig = {
        ...config,
        size: config.size || 'lg',
        footerButtons: [
            {
                text: 'Cancelar',
                class: 'btn-secondary',
                id: 'btn-cancel-form'
            },
            {
                text: 'Salvar',
                class: 'btn-primary',
                id: 'btn-submit-form',
                onClick: onSubmit
            }
        ]
    };
    
    return new ModalManager(modalConfig).create(title, formHtml);
}

/**
 * Função de compatibilidade com código existente
 */
export function createModal(title, content) {
    return new ModalManager().create(title, content);
}

/**
 * Utilitários para notificações rápidas
 */
export const notify = {
    success: (message, title = 'Sucesso') => createSuccessModal(title, message, { size: 'sm' }),
    error: (message, title = 'Erro') => createErrorModal(title, message, { size: 'sm' }),
    warning: (message, title = 'Atenção') => createWarningModal(title, message, { size: 'sm' }),
    info: (message, title = 'Informação') => createInfoModal(title, message, { size: 'sm' })
};
